import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import { updateUser } from 'src/models/user/user';
import { isTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import toast from 'src/utils/toast';

import { DreamcoatIntroModal } from './DreamcoatIntroModal';

import type { User } from 'src/models/user/_types';

/**
 * Per-staff-user control to opt in/out of the new Dreamcoat navigation.
 * Only rendered when the gym has the `Dreamcoat Opt In` flag, the
 * viewer is staff, and the nav is NOT force-enabled by `Dreamcoat Frame` (that
 * mode has no toggle). Persists the choice to the user and refetches the
 * platform session so the server's effective-Dreamcoat tabs and the client
 * chrome stay in sync.
 *
 * The first opt-in attempt opens a one-time intro modal; after the user has seen
 * it (`dreamcoatIntroSeen`), later clicks switch directly.
 */
export const DreamcoatNavToggle: React.FC = () => {
  const { session, user } = useOptionalSession();
  const queryClient = useQueryClient();
  const [introOpen, setIntroOpen] = useState(false);
  const { t } = useTranslation('translation', {
    keyPrefix: 'dreamcoat.navToggle',
  });

  const canToggle =
    !!session &&
    !!user &&
    isTrainer(user) &&
    hasFeatureFlag(session, FeatureFlag.DreamcoatOptIn) &&
    !hasFeatureFlag(session, FeatureFlag.DreamcoatFrame);

  const optedIn = !!user?.dreamcoatNav;
  const introSeen = !!user?.dreamcoatIntroSeen;

  const navMutation = useMutation({
    mutationFn: (changes: Partial<User>) => {
      if (!user?.id) {
        throw new Error('Cannot update Dreamcoat nav without a user');
      }
      return updateUser(user.id, changes);
    },
    onSuccess: () =>
      Promise.all([
        queryClient.refetchQueries({ queryKey: ['session'] }),
        queryClient.refetchQueries({ queryKey: ['currentUser'] }),
      ]),
    onError: () => {
      toast.error('Could not switch navigation. Please try again.');
    },
  });

  if (!canToggle) {
    return null;
  }

  const onToggleClick = () => {
    if (optedIn) {
      navMutation.mutate({ dreamcoatNav: false });
    } else if (introSeen) {
      navMutation.mutate({ dreamcoatNav: true });
    } else {
      setIntroOpen(true);
    }
  };

  // Which modal action is in-flight (so only the clicked button spins).
  const pendingNav = navMutation.variables?.dreamcoatNav;

  return (
    <>
      <Button
        variant="borderless"
        color="primary"
        size="small"
        loading={navMutation.isPending && !introOpen}
        onClick={onToggleClick}
        sx={
          optedIn
            ? {
                '&&': { color: 'neutral.text' },
                '&&:hover': { bgcolor: 'primary.backgroundSoft', color: 'primary.text' },
              }
            : {
                '&&:hover': { bgcolor: 'primary.backgroundSoft' },
              }
        }
      >
        {optedIn ? t('backToOldNav') : t('tryNewNav')}
      </Button>
      <DreamcoatIntroModal
        open={introOpen}
        tryItOutLoading={navMutation.isPending && pendingNav === true}
        maybeLaterLoading={navMutation.isPending && pendingNav === undefined}
        onClose={() => setIntroOpen(false)}
        onMaybeLater={() =>
          navMutation.mutate(
            { dreamcoatIntroSeen: true },
            { onSuccess: () => setIntroOpen(false) }
          )
        }
        onTryItOut={() =>
          navMutation.mutate(
            { dreamcoatNav: true, dreamcoatIntroSeen: true },
            { onSuccess: () => setIntroOpen(false) }
          )
        }
      />
    </>
  );
};
